import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import cn from 'classnames';

import { BsChevronDown } from 'react-icons/bs';
import { PiCheckFatFill } from 'react-icons/pi';

import { IOSearchableDropdownProps, IDropdownItem } from './OSearchableDropdown.types';

import styles from './OSearchableDropdown.module.scss';

const OSearchableDropdown: React.FC<IOSearchableDropdownProps> = ({
  items,
  selectedValue,
  label,
  placeholder = 'Select an option',
  disabled = false,
  searchable = true,
  selectedIcon,
  className,
  size = 'medium',
  fullWidth = false,
  menuFullWidth = true,
  highlightSelected = false,
  classes,
  onChange,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuHeight, setMenuHeight] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find the selected item
  const selectedItem = items.find((item: IDropdownItem) => item.value === selectedValue);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    return items.filter((item: IDropdownItem) => item.label.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [items, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        // For portal-based menus, we need to check if the click is outside both the dropdown and the menu
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      // Add a small delay to prevent immediate closing when opening
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Reset search and menu height when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setMenuHeight(null);
    }
  }, [isOpen]);

  // Handle positioning of the dropdown menu
  useEffect(() => {
    // Capture ref values at the time of effect creation to avoid stale refs in cleanup
    const currentMenuRef = menuRef.current;

    const updateMenuPosition = () => {
      if (isOpen && dropdownRef.current && currentMenuRef) {
        // Check if menu still exists in DOM
        if (!document.body.contains(currentMenuRef)) {
          return;
        }

        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        let actualMenuHeight = menuHeight;

        // Only calculate menu height if we haven't stored it yet
        // This ensures the height remains consistent during search operations
        if (actualMenuHeight === null) {
          // Force a reflow to get actual menu dimensions
          currentMenuRef.style.visibility = 'hidden';
          currentMenuRef.style.display = 'block';

          // Get actual menu dimensions
          const menuRect = currentMenuRef.getBoundingClientRect();
          actualMenuHeight = menuRect.height;

          // Store the height for future reference
          setMenuHeight(actualMenuHeight);

          // Reset visibility
          currentMenuRef.style.visibility = '';
          currentMenuRef.style.display = '';
        }

        // Define gap between dropdown and menu
        const GAP = 8; // Increased gap for better visual separation

        // Check if there's enough space below
        const spaceBelow = viewportHeight - dropdownRect.bottom;
        const spaceAbove = dropdownRect.top;

        // Set position based on available space
        // Use the preserved menu height to ensure consistent positioning during search
        if (spaceBelow < actualMenuHeight && spaceAbove > spaceBelow) {
          // Position menu above dropdown with gap
          currentMenuRef.style.top = `${dropdownRect.top - actualMenuHeight - GAP}px`;
          currentMenuRef.style.left = `${dropdownRect.left}px`;
          currentMenuRef.style.width = menuFullWidth ? `${dropdownRect.width}px` : 'auto';
        } else {
          // Position menu below dropdown with gap
          currentMenuRef.style.top = `${dropdownRect.bottom + GAP}px`;
          currentMenuRef.style.left = `${dropdownRect.left}px`;
          currentMenuRef.style.width = menuFullWidth ? `${dropdownRect.width}px` : 'auto';
        }
      }
    };

    updateMenuPosition();

    // Update position on window resize and scroll
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition);
    };
  }, [isOpen, menuFullWidth, menuHeight]); // Dependencies intentionally exclude filteredItems to preserve height during search

  // Preserve menu list container height during search
  useEffect(() => {
    // Capture ref value at time of effect creation to avoid stale refs in cleanup
    const currentMenuRef = menuRef.current;

    // Apply height preservation whenever menu is open and we have a stored height
    if (isOpen && currentMenuRef && menuHeight !== null) {
      const menuListContainer = currentMenuRef.querySelector(`.${styles.menuListContainer}`) as HTMLElement;
      if (menuListContainer) {
        // Set a fixed height to prevent container from shrinking during search
        // Subtract padding to account for menuPaper padding
        menuListContainer.style.maxHeight = `${menuHeight - 8}px`; // Account for 4px padding on top and bottom
      }
    }

    return () => {
      if (currentMenuRef) {
        const menuListContainer = currentMenuRef.querySelector(`.${styles.menuListContainer}`) as HTMLElement;
        if (menuListContainer) {
          menuListContainer.style.maxHeight = '';
        }
      }
    };
  }, [isOpen, menuHeight, menuRef]);

  // Update position when dropdown size changes
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Trigger position update when dropdown size changes
      window.dispatchEvent(new Event('resize'));
    });

    resizeObserver.observe(dropdownRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen]);

  // Focus search input when menu opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);

  const toggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    if (isOpen) {
      setSearchQuery('');
    }
  }, [disabled, isOpen]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      onSearchChange?.(value);
    },
    [onSearchChange]
  );

  const handleItemClick = useCallback(
    (item: IDropdownItem) => {
      onChange(item.value);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onChange]
  );

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className={cn(
        styles.oSelectContainer, 
        className,
        {[styles.fullWidth]: fullWidth}
      )}
    >
      {label && <label className={cn(styles.label, classes?.customLabel)}>{label}</label>}
      <div
        ref={dropdownRef}
        className={cn(styles.selectContainer, styles[size], classes?.selectContainer, {
          [styles.open]: isOpen,
          [styles.disabled]: disabled,
          [styles.fullWidth]: fullWidth,
        })}
        onClick={toggleDropdown}
      >
        <div className={cn(styles.selectInput, classes?.customInput)}>
          {selectedIcon ? (
            <span className={styles.listItemIcon}>{selectedIcon}</span>
          ) : (
            selectedItem?.icon && <span className={styles.listItemIcon}>{selectedItem.icon}</span>
          )}
          <span className={styles.selectedText}>{selectedItem ? selectedItem.label : placeholder}</span>
        </div>
        <BsChevronDown className={cn(styles.chevronDown, { [styles.open]: isOpen })} size={13} />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={cn(styles.menuPaper, classes?.customMenu, {
              [styles.menuFullWidth]: menuFullWidth,
              [styles.menuAutoWidth]: !menuFullWidth,
            })}
          >
            <div className={cn(styles.menuRoot, classes?.customMenuBackground)}>
              <div className={cn(styles.menuListContainer, { [styles.preserveHeight]: searchQuery.length > 0 })}>
                {searchable && (
                  <div className={styles.searchInputContainer}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      id='search'
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onClick={handleSearchClick}
                      placeholder="Search..."
                      className={styles.searchInput}
                    />
                  </div>
                )}
                <div className={styles.menuItems}>
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item: IDropdownItem) => (
                      <div
                        key={item.value}
                        className={cn(styles.menuItem, {
                          [styles.selected]: item.value === selectedValue,
                          [styles.highlighted]: highlightSelected && item.value === selectedValue,
                        })}
                        onClick={() => handleItemClick(item)}
                      >
                        {item.icon && <span className={styles.listItemIcon}>{item.icon}</span>}
                        <span className={styles.menuItemText}>{item.label}</span>
                        {highlightSelected && item.value === selectedValue && (
                          <span className={styles.listItemIcon}>
                            <PiCheckFatFill size={20} color="#00acfb" />
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptyMenu}>
                      <span className={styles.menuItemText}>No results found</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OSearchableDropdown;
